"use client";

import { useState, type ReactNode } from "react";
import { useModalHistoryEntry } from "@/shared/hooks/useModalHistoryEntry";
import { IoCloseOutline } from "react-icons/io5";
import { RiArrowLeftLine } from "react-icons/ri";

export interface SideDrawerProps {
    open: boolean;
    /** Fecha o drawer — renderiza o X à direita e habilita o clique no backdrop. */
    onClose?: () => void;
    /** Volta um nível — renderiza a seta à esquerda do título. */
    onBack?: () => void;
    title: ReactNode;
    /** Ex.: badge de contagem, ao lado do título. */
    headerExtra?: ReactNode;
    /** Slot fixo entre a barra de título e o corpo (ex.: campo de busca). */
    subheader?: ReactNode;
    /** Corpo — o shell já provê o container rolável. */
    children: ReactNode;
    /** Rodapé fixo — já sai com a moldura padrão (borda + fundo). */
    footer?: ReactNode;
    "aria-label": string;
}

/**
 * Shell compartilhado dos drawers laterais (carrinho, busca, ...) — backdrop
 * + painel que desliza pela direita (fullscreen no mobile, sem backdrop),
 * barra de título em `primary`. Editar aqui muda todos os drawers.
 *
 * Monte UMA vez e controle por `open`: o shell segura o unmount até a
 * animação de saída terminar (`animationend`), senão o painel some sem
 * animar.
 *
 * ── Gesto de voltar no mobile ─────────────────────────────────────────────
 * Com o drawer aberto, o arrasto lateral acionava o "voltar" do navegador e
 * levava o visitante para a página anterior. No iOS esse gesto é do SISTEMA —
 * CSS nenhum desliga. A saída não é bloquear, é dar um DESTINO a ele: o
 * `useModalHistoryEntry` empurra uma entrada de histórico enquanto o drawer
 * está aberto, então voltar FECHA o drawer em vez de sair da página. Mesmo
 * comportamento de app nativo, e igual nos dois sistemas.
 */
const noop = () => {};

export function SideDrawer({
    open,
    onClose,
    onBack,
    title,
    headerExtra,
    subheader,
    children,
    footer,
    "aria-label": ariaLabel,
}: SideDrawerProps) {
    const [render, setRender] = useState(open);
    const closing = render && !open;

    // Abriu → monta (estado derivado no render, sem effect). O desmonte fica
    // por conta do animationend do slide-out (mantém `render` até lá).
    if (open && !render) setRender(true);

    const requestClose = onClose ?? onBack;
    // Antes do early return: hook não pode ficar atrás de condicional.
    const dismiss = useModalHistoryEntry(open, requestClose ?? noop);

    if (!render) return null;

    // Botões "puros" (não o Button/.btn do daisyUI): o `.btn` traz altura/
    // line-height próprios que desalinham o ícone da linha de texto do
    // título — aqui o alinhamento vertical é garantido pelo items-center.
    const iconButtonClass =
        "flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-primary-content transition-colors duration-150 hover:bg-primary-content/15 active:scale-90";

    return (
        <>
            <div
                className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm max-lg:hidden ${
                    closing ? "animate-drawer-fade-out" : "animate-drawer-fade-in"
                }`}
                aria-hidden="true"
                onClick={dismiss}
            />

            <aside
                className={`fixed right-0 top-0 z-50 flex h-screen w-[min(520px,100vw)] flex-col overflow-hidden bg-base-100 supports-[height:100dvh]:h-dvh shadow-[-4px_0_24px_color-mix(in_srgb,black_15%,transparent)] max-lg:w-full lg:rounded-l-2xl ${
                    closing ? "animate-drawer-slide-out-right" : "animate-drawer-slide-in-right"
                }`}
                aria-label={ariaLabel}
                onAnimationEnd={(e) => {
                    if (closing && e.target === e.currentTarget) setRender(false);
                }}
            >
                {/* Barra de título */}
                <div className="flex h-13 shrink-0 items-center gap-2 bg-primary px-3 text-primary-content">
                    {onBack && (
                        <button
                            type="button"
                            className={iconButtonClass}
                            aria-label="Voltar"
                            onClick={onBack}
                        >
                            <RiArrowLeftLine size={20} />
                        </button>
                    )}
                    <span
                        className={`flex min-w-0 flex-1 select-none items-center gap-2 truncate text-sm font-bold uppercase leading-none tracking-widest ${
                            onBack ? "" : "pl-2"
                        }`}
                    >
                        {title}
                        {headerExtra}
                    </span>
                    {onClose && (
                        <button
                            type="button"
                            className={iconButtonClass}
                            aria-label="Fechar"
                            onClick={dismiss}
                        >
                            <IoCloseOutline size={22} />
                        </button>
                    )}
                </div>

                {subheader}

                {/* `overscroll-contain`: o shell não trava o scroll da página —
                    sem isto, chegar ao fim da lista continua rolando o fundo.
                    `flex flex-col`: listas empilham do topo normalmente, e um
                    conteúdo vazio (EmptyState) centraliza com `my-auto`. */}
                <div className="flex min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-contain">
                    {children}
                </div>

                {footer && (
                    <div className="flex shrink-0 flex-col gap-3 border-t border-base-200 bg-base-200/60 px-5 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
                        {footer}
                    </div>
                )}
            </aside>
        </>
    );
}
